import type { Car, CreateCarInput } from '@taler/api-client'
import { carKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { problemDetail } from '../internal/problem.ts'

export interface UseCarsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseCars {
  readonly cars: ComputedRef<readonly Car[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly hasCreateError: ComputedRef<boolean>
  readonly createErrorDetail: ComputedRef<string | undefined>
  create(input: CreateCarInput): Promise<Car>
  setPrimary(carId: string): Promise<void>
  retry(): void
}

export function useCars(options: UseCarsOptions): UseCars {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const carsQuery = useQuery({
    queryKey: computed(() => carKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.cars.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): Promise<void> {
    const id = options.ledgerId()
    if (id === undefined) return Promise.resolve()
    return Promise.all(
      carKeys.invalidatedByWrite(id).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateCarInput) => api.cars.create(options.ledgerId()!, input),
    onSuccess: () => invalidate(),
  })

  const setPrimaryMutation = useMutation({
    mutationFn: (carId: string) => api.cars.setPrimary(options.ledgerId()!, carId),
    onSuccess: () => invalidate(),
  })

  return {
    cars: computed(() => carsQuery.data.value ?? []),
    isLoading: computed(() => active.value && carsQuery.isPending.value),
    error: computed(() => carsQuery.error.value),
    retrying: retrying(carsQuery),
    isCreating: computed(() => createMutation.isPending.value),
    hasCreateError: computed(() => createMutation.error.value !== null),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    create: (input) => createMutation.mutateAsync(input),
    setPrimary: (carId) => setPrimaryMutation.mutateAsync(carId),
    retry: () => {
      void carsQuery.refetch()
    },
  }
}
